import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './AdminMessages.css';

export const AdminMessages: React.FC = () => {
  const { language } = useLanguage();
  
  const [conversations, setConversations] = useState<any[]>([]);
  const [activeUser, setActiveUser] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchConversations = async () => {
    try {
      const res = await apiService.getAdminConversations();
      setConversations(res.data || []);
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
  };

  const fetchMessages = async (userId: string) => {
    try {
      const res = await apiService.getConversation(userId);
      setMessages(res.data || []);
      await apiService.markMessagesAsRead(userId);
      // Refresh conversation list to clear unread badge
      fetchConversations();
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000); // Poll list every 10s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeUser) {
      fetchMessages(activeUser._id);
      const interval = setInterval(() => fetchMessages(activeUser._id), 5000);
      return () => clearInterval(interval);
    }
  }, [activeUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeUser) return;

    try {
      const sentMsg = await apiService.sendMessage(newMessage, activeUser._id);
      setMessages(prev => [...prev, sentMsg.data]);
      setNewMessage('');
      fetchConversations();
    } catch (err) {
      console.error('Failed to send message', err);
    }
  };

  return (
    <div className="admin-messages-container">
      <div className="conversations-sidebar">
        <h3>{language === 'vi' ? 'Hội thoại' : 'Conversations'}</h3>
        <div className="conversation-list">
          {conversations.map((conv) => (
            <div 
              key={conv.user._id} 
              className={`conversation-item ${activeUser?._id === conv.user._id ? 'active' : ''}`}
              onClick={() => setActiveUser(conv.user)}
            >
              <div className="conv-user-info">
                <strong>{conv.user.name || conv.user.email}</strong>
                <span className="conv-preview">{conv.latestMessage}</span>
              </div>
              {conv.unreadCount > 0 && <div className="unread-badge">{conv.unreadCount}</div>}
            </div>
          ))}
          {conversations.length === 0 && <p className="no-data">No messages</p>}
        </div>
      </div>

      <div className="admin-chat-main">
        {activeUser ? (
          <>
            <div className="chat-header">
              <h2>{activeUser.name || activeUser.email}</h2>
            </div>
            <div className="chat-window">
              <div className="message-list">
                {messages.map((msg) => {
                  const isMine = msg.senderId !== activeUser._id;
                  return (
                    <div key={msg.id} className={`message-bubble ${isMine ? 'mine' : 'theirs'}`}>
                      <div className="msg-content">{msg.content}</div>
                      <div className="msg-time">{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
            <form className="chat-input-area" onSubmit={handleSend}>
              <input 
                type="text" 
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply..."
              />
              <button type="submit" disabled={!newMessage.trim()}>Send</button>
            </form>
          </>
        ) : (
          <div className="empty-chat-state">
            <p>Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};
